/**
 * Estado en memoria.
 *
 * Acá vive solo lo que **no** es un dato del negocio: quién inició sesión, qué
 * mesa está seleccionada, qué modal está abierto. Todo lo demás —órdenes,
 * ítems, catálogo— vive en IndexedDB y se lee con `useLiveQuery`.
 *
 * Esa separación es deliberada: si el estado del pedido viviera en React, un
 * refresh accidental del navegador con el restaurante lleno borraría las
 * comandas.
 */

import { create } from 'zustand';
import { token } from '../api/cliente';
import type { Restaurante, Usuario } from '../tipos/contrato';

const CLAVE_SESION = 'casanugget.sesion';

interface SesionGuardada {
  usuario: Usuario;
  restaurante: Restaurante;
}

function leerSesionGuardada(): SesionGuardada | null {
  try {
    const crudo = localStorage.getItem(CLAVE_SESION);
    return crudo ? (JSON.parse(crudo) as SesionGuardada) : null;
  } catch {
    return null;
  }
}

export type Modal = null | 'productos' | 'pago' | 'datosMesa';

interface Estado {
  usuario: Usuario | null;
  restaurante: Restaurante | null;
  /** Hay token: la app puede trabajar aunque en este momento no haya red. */
  autenticado: boolean;

  mesaSeleccionada: string | null;
  ordenSeleccionada: string | null;
  modal: Modal;
  filtroMesas: 'todas' | 'libre' | 'ocupada' | 'cuenta';
  aviso: string | null;
  enLinea: boolean;
  pendientesSync: number;

  iniciarSesion: (usuario: Usuario, restaurante: Restaurante, jwt: string) => void;
  cerrarSesion: () => void;
  seleccionar: (mesaId: string | null, ordenId: string | null) => void;
  abrirModal: (m: Modal) => void;
  setFiltro: (f: Estado['filtroMesas']) => void;
  avisar: (texto: string | null) => void;
  setConexion: (enLinea: boolean, pendientes?: number) => void;
}

export const useSesion = create<Estado>((set) => {
  const guardada = leerSesionGuardada();

  return {
    usuario: guardada?.usuario ?? null,
    restaurante: guardada?.restaurante ?? null,
    autenticado: Boolean(token.leer() && guardada),

    mesaSeleccionada: null,
    ordenSeleccionada: null,
    modal: null,
    filtroMesas: 'todas',
    aviso: null,
    enLinea: navigator.onLine,
    pendientesSync: 0,

    iniciarSesion: (usuario, restaurante, jwt) => {
      token.guardar(jwt);
      localStorage.setItem(CLAVE_SESION, JSON.stringify({ usuario, restaurante }));
      set({ usuario, restaurante, autenticado: true });
    },

    cerrarSesion: () => {
      token.borrar();
      localStorage.removeItem(CLAVE_SESION);
      // Ojo: NO se borra IndexedDB. Si quedan órdenes sin sincronizar, tienen
      // que sobrevivir al cierre de sesión; se drenan cuando alguien vuelva
      // a entrar. Perder ventas por cerrar sesión sería inaceptable.
      set({
        usuario: null,
        restaurante: null,
        autenticado: false,
        mesaSeleccionada: null,
        ordenSeleccionada: null,
        modal: null,
      });
    },

    seleccionar: (mesaId, ordenId) =>
      set({ mesaSeleccionada: mesaId, ordenSeleccionada: ordenId }),

    abrirModal: (modal) => set({ modal }),
    setFiltro: (filtroMesas) => set({ filtroMesas }),
    avisar: (aviso) => set({ aviso }),
    setConexion: (enLinea, pendientes) =>
      set((s) => ({ enLinea, pendientesSync: pendientes ?? s.pendientesSync })),
  };
});
